// Public, no-login Exit Interview form — opened via the URL emailed to
// an exiting employee: /#exit-interview?token=<64-hex>. Standalone, same
// pattern as PublicIntake/PublicApply. The question set (wording + field
// type) comes entirely from the backend (shared/exitInterviewQuestions.js
// on the server) so this component never hardcodes a question — it just
// renders whatever `type` each one says: 'text' (textarea), 'yesno'
// (Yes/No buttons), 'rating10' (1-10 picker).

import React, { useState, useEffect } from "react";
import { CheckCircle, AlertTriangle, Star } from "lucide-react";
import { BASE } from "../api";

export default function PublicExitInterview() {
  const rawQuery = window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?'))
    : window.location.search;
  const token = new URLSearchParams(rawQuery).get('token') || '';

  const [state, setState] = useState('loading'); // loading | ready | already | expired | error | done
  const [errorMsg, setErrorMsg] = useState('');
  const [canRetry, setCanRetry] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [employeeName, setEmployeeName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [priorAnswers, setPriorAnswers] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setErrorMsg('Missing link token. Please re-open the link HR sent you.'); return; }
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/exit-interview/${token}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setErrorMsg(data.error || 'This link is not valid.');
          setCanRetry(res.status >= 500);
          setState(res.status === 410 ? 'expired' : 'error');
          return;
        }
        setEmployeeName(data.employeeName || '');
        setQuestions(data.questions || []);
        if (data.alreadySubmitted) {
          setPriorAnswers(data.answers || {});
          setState('already');
        } else {
          setState('ready');
        }
      } catch (e) {
        if (cancelled) return;
        setErrorMsg('Could not reach the server. Check your connection and try again.');
        setCanRetry(true);
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, retryTick]);

  const set = (k, v) => setAnswers(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr('');
    const missing = questions.filter(q => !answers[q.key] && answers[q.key] !== 0);
    if (missing.length) { setSubmitErr('Please answer every question before submitting.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/api/exit-interview/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setState('done');
      setErrorMsg(data.message);
    } catch (e) {
      setSubmitErr(e instanceof TypeError
        ? "Couldn't reach the server — check your connection and try submitting again. Nothing you've entered was lost."
        : (e.message || 'Submission failed. Please try again.'));
    } finally { setSubmitting(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
        {children}
      </div>
    </div>
  );

  if (state === 'loading') {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  }
  if (state === 'error' || state === 'expired') {
    return (
      <Shell>
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4"><AlertTriangle size={28} className="text-amber-600" /></div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">{state === 'expired' ? 'Link no longer active' : 'Something went wrong'}</h1>
        <p className="text-sm text-slate-600">{errorMsg || 'Please reach out to HR.'}</p>
        {canRetry && <button onClick={() => setRetryTick(t => t + 1)} className="mt-4 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">Try Again</button>}
      </Shell>
    );
  }
  if (state === 'done') {
    return (
      <Shell>
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><CheckCircle size={28} className="text-emerald-600" /></div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Thank you</h1>
        <p className="text-sm text-slate-600 leading-relaxed">{errorMsg || 'Your exit interview has been submitted.'}</p>
      </Shell>
    );
  }
  if (state === 'already') {
    return (
      <Shell>
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><CheckCircle size={28} className="text-emerald-600" /></div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Already submitted</h1>
        <p className="text-sm text-slate-600">You've already completed this exit interview — thank you for your feedback.</p>
      </Shell>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <form onSubmit={submit} className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
        <h1 className="text-2xl font-extrabold text-slate-900">Exit Interview</h1>
        <p className="text-sm text-slate-600 mt-1 mb-6">
          {employeeName ? `Hi ${employeeName}, ` : ''}we'd appreciate a few honest minutes of your feedback. Your responses help us improve.
        </p>

        <div className="flex flex-col gap-6">
          {questions.map(q => (
            <div key={q.key}>
              <div className="text-[14px] font-semibold text-slate-800 mb-2">{q.label}</div>
              {q.type === 'text' && (
                <textarea
                  value={answers[q.key] || ''}
                  onChange={e => set(q.key, e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 resize-y"
                />
              )}
              {q.type === 'yesno' && (
                <div className="flex gap-2">
                  {['Yes', 'No'].map(opt => (
                    <button
                      type="button" key={opt}
                      onClick={() => set(q.key, opt)}
                      className={`px-5 py-[7px] rounded-lg text-[14px] font-bold border cursor-pointer transition-colors ${
                        answers[q.key] === opt ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-emerald-400'
                      }`}
                    >{opt}</button>
                  ))}
                </div>
              )}
              {q.type === 'rating10' && (
                <div className="flex gap-[3px] flex-wrap">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                    <button
                      type="button" key={n}
                      onClick={() => set(q.key, n)}
                      title={`${n}/10`}
                      className="p-[2px] bg-transparent border-0 cursor-pointer"
                    >
                      <Star size={22} fill={(answers[q.key] || 0) >= n ? '#F59E0B' : 'none'} color={(answers[q.key] || 0) >= n ? '#F59E0B' : '#CBD5E1'} />
                    </button>
                  ))}
                  {answers[q.key] && <span className="ml-2 text-[13px] font-bold text-slate-600 self-center">{answers[q.key]}/10</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {submitErr && <div className="mt-4 text-[14px] text-red-600">{submitErr}</div>}
        <button type="submit" disabled={submitting} className="mt-6 w-full px-4 py-3 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-60 cursor-pointer">
          {submitting ? 'Submitting…' : 'Submit Exit Interview'}
        </button>
      </form>
    </div>
  );
}
